import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import EmmyAssistant from "./components/EmmyAssistant";
import WhatsAppButton from "./components/WhatsAppButton";
import NewsNotifier from "./components/NewsNotifier";
import LandingPage from "./pages/LandingPage";
import CollegePredictor from "./pages/CollegePredictor";
import CollegeList from "./pages/CollegeList";
import CollegeDetail from "./pages/CollegeDetail";
import CounsellingTracker from "./pages/CounsellingTracker";
import SchedulePage from "./pages/SchedulePage";
import AdminPanel from "./pages/AdminPanel";
import AdminConfig from "./pages/AdminConfig";
import AdminDataHealth from "./pages/AdminDataHealth";
import AdminCrawlers from "./pages/AdminCrawlers";
import AdminLogs from "./pages/AdminLogs";
import AdminCrawledDocs from "./pages/AdminCrawledDocs";
import AdminNmcRaw from "./pages/AdminNmcRaw";
import AdminLeads from "./pages/AdminLeads";
import AdminLeadTemplates from "./pages/AdminLeadTemplates";
import AdminSubscribers from "./pages/AdminSubscribers";
import AdminPredictorFees from "./pages/AdminPredictorFees";
import AdminArchitecture from "./pages/AdminArchitecture";
import AdminMaster from "./pages/AdminMaster";
import AdminMeritList from "./pages/AdminMeritList";
import AdminIngestionDiary from "./pages/AdminIngestionDiary";
import AdminMigrations from "./pages/AdminMigrations";
import AdminOpenStateMapping from "./pages/AdminOpenStateMapping";
import StatsPage from "./pages/StatsPage";
import About from "./pages/About";
import PlansSubscription from "./pages/PlansSubscription";
import PlansPriorityCounselling from "./pages/PlansPriorityCounselling";
import PlansNRICounselling from "./pages/PlansNRICounselling";
import PlansAffiliate from "./pages/PlansAffiliate";
import { AuthProvider } from "./lib/AuthProvider";
import { ProtectedAdminRoute } from "./components/ProtectedAdminRoute";
import AdminLayout from "./components/AdminLayout";

// One helper so admin routes read cleanly and can't accidentally be added
// unprotected (all wrapped in ProtectedAdminRoute below).
const Admin = ({ children }) => <ProtectedAdminRoute><AdminLayout>{children}</AdminLayout></ProtectedAdminRoute>;

// Legacy /predict → canonical /ug-neet/predict (preserves query string).
const PredictRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/ug-neet/predict${search}`} replace />;
};

// SEO alias: /ug-neet-2026/predict (or any /ug-neet-YYYY/predict) renders the
// same predictor page. React Router can't do a partial-segment param, so we
// match the whole first segment and guard it against the ug-neet(-YYYY) shape;
// anything else falls back to the canonical route. The canonical
// /ug-neet/predict route is more specific and always wins over this.
const SeoPredict = () => {
  const { seoSlug } = useParams();
  const { search } = useLocation();
  if (/^ug-neet(-\d{4})?$/.test(seoSlug || "")) return <CollegePredictor />;
  return <Navigate to={`/ug-neet/predict${search}`} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen flex flex-col bg-white">
          <Navbar />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/ug-neet/predict" element={<CollegePredictor />} />
              {/* SEO alias: /ug-neet-2026/predict → same page (canonical points to /ug-neet/predict) */}
              <Route path="/:seoSlug/predict" element={<SeoPredict />} />
              {/* Legacy path kept as a redirect so old links/bookmarks/SEO don't break */}
              <Route path="/predict" element={<PredictRedirect />} />
              <Route path="/colleges" element={<CollegeList />} />
              <Route path="/colleges/:code/*" element={<CollegeDetail />} />
              <Route path="/colleges/:code" element={<CollegeDetail />} />
              <Route path="/counselling" element={<CounsellingTracker />} />
              <Route path="/counselling/tracker/:body/:year" element={<CounsellingTracker />} />
              <Route path="/stats" element={<StatsPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/plans/subscription" element={<PlansSubscription />} />
              <Route path="/plans/priority-counselling" element={<PlansPriorityCounselling />} />
              <Route path="/plans/nri-counselling" element={<PlansNRICounselling />} />
              <Route path="/plans/affiliate" element={<PlansAffiliate />} />
              <Route path="/counselling/schedule/:bodySlug/:slug" element={<SchedulePage />} />
              <Route path="/counselling/:bodySlug/:slug" element={<SchedulePage />} />
              <Route path="/admin" element={<Admin><AdminPanel /></Admin>} />
              <Route path="/admin/config" element={<Admin><AdminConfig /></Admin>} />
              <Route path="/admin/data-health" element={<Admin><AdminDataHealth /></Admin>} />
              <Route path="/admin/crawlers" element={<Admin><AdminCrawlers /></Admin>} />
              <Route path="/admin/logs" element={<Admin><AdminLogs /></Admin>} />
              <Route path="/admin/crawled-docs" element={<Admin><AdminCrawledDocs /></Admin>} />
              <Route path="/admin/nmc-raw" element={<Admin><AdminNmcRaw /></Admin>} />
              <Route path="/admin/leads" element={<Admin><AdminLeads /></Admin>} />
              <Route path="/admin/leads/templates" element={<Admin><AdminLeadTemplates /></Admin>} />
              <Route path="/admin/subscribers" element={<Admin><AdminSubscribers /></Admin>} />
              <Route path="/admin/predictor-fees" element={<Admin><AdminPredictorFees /></Admin>} />
              <Route path="/admin/architecture" element={<Admin><AdminArchitecture /></Admin>} />
              <Route path="/admin/master" element={<Admin><AdminMaster /></Admin>} />
              <Route path="/admin/merit-list" element={<Admin><AdminMeritList /></Admin>} />
              <Route path="/admin/ingestion-diary" element={<Admin><AdminIngestionDiary /></Admin>} />
              <Route path="/admin/migrations" element={<Admin><AdminMigrations /></Admin>} />
              <Route path="/admin/open-states" element={<Admin><AdminOpenStateMapping /></Admin>} />
            </Routes>
          </main>
          <Footer />
          <NewsNotifier />
          <EmmyAssistant />
          <WhatsAppButton />
          <Toaster position="bottom-right" richColors closeButton />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
